import type { AttentionKind, OverviewResponse } from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { getOverview } from '../api/overview.js';
import { useSession } from '../auth/session-context.js';
import { effectiveTimezone } from '../lib/time.js';
import { NAV_SECTION_LABEL, visibleNavItems, type NavItem, type NavSection } from '../nav/items.js';
import { Button } from '../ui/Button.js';
import { BellIcon, LogOutIcon, ShieldIcon } from '../ui/icons.js';
import { ThemeSwitch } from './ThemeSwitch.js';

/**
 * The frame every signed-in page sits in (knowledge doc section 17): the sidebar
 * is the navigation, filtered to the role; the top bar holds where you are, what
 * waits, the time zone, the theme, who is signed in and the way out.
 *
 * A sidebar rather than a top bar, because section 15's dashboard routes are a
 * dozen destinations by the end of phase 9 and a row of tabs stops working long
 * before that. It folds to a scrolling strip on a narrow screen.
 *
 * **Grouped, with counts (P11.4).** Monitor, Respond, Configure, Admin — and a
 * count beside the pages where something waits for a person: decisions on the
 * Overview, unread high alerts on Events, suggestions waiting. The counts are the
 * overview's own (`GET /overview`, the same cached query the landing page reads,
 * refreshed each minute), so the sidebar and the page never disagree.
 */

const SECTIONS: Exclude<NavSection, 'account'>[] = ['monitor', 'respond', 'configure', 'admin'];
const REFRESH_MS = 60_000;

/** What waits on each page, from the overview's counts. */
function countsOf(overview: OverviewResponse | undefined): Map<string, number> {
  const counts = new Map<string, number>();
  if (overview === undefined) return counts;
  const of = (kind: AttentionKind): number => overview.attentionCounts[kind] ?? 0;
  const all = Object.values(overview.attentionCounts).reduce((sum, n) => sum + n, 0);
  if (all > 0) counts.set('/', all);
  if (of('event') > 0) counts.set('/events', of('event'));
  if (of('suggestion') > 0) counts.set('/suggestions', of('suggestion'));
  return counts;
}

/** Pages the sidebar does not list, but that belong under a section. */
const OFF_MENU: { prefix: string; section: string | undefined; label: string }[] = [
  { prefix: '/addresses', section: NAV_SECTION_LABEL.monitor, label: 'Addresses' },
  { prefix: '/account', section: undefined, label: 'Your account' },
];

/** Where a path sits: the longest sidebar destination it is under, else an off-menu page. */
function crumbFor(
  pathname: string,
  items: readonly NavItem[],
): { section: string | undefined; label: string } {
  const under = (to: string): boolean =>
    to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
  const nav = items
    .filter((item) => item.section !== 'account' && under(item.to))
    .sort((x, y) => y.to.length - x.to.length)[0];
  if (nav !== undefined && nav.section !== 'account') {
    return { section: NAV_SECTION_LABEL[nav.section], label: nav.label };
  }
  const other = OFF_MENU.find((entry) => under(entry.prefix));
  return other ?? { section: undefined, label: 'Bartizo' };
}

function NavEntry({ item, count }: { item: NavItem; count: number | undefined }): ReactNode {
  return (
    <li>
      <NavLink to={item.to} end={item.to === '/'}>
        <item.icon className="nav-icon" />
        <span className="nav-label">{item.label}</span>
        {count !== undefined && (
          <>
            <span className="nav-count" aria-hidden="true">
              {count > 99 ? '99+' : count}
            </span>
            <span className="visually-hidden">, {count} waiting</span>
          </>
        )}
      </NavLink>
    </li>
  );
}

export function AppShell({ children }: { children: ReactNode }): ReactNode {
  const { session, signOut } = useSession();
  const overview = useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
    refetchInterval: REFRESH_MS,
    enabled: session !== null,
    // The sidebar is a hint: a failed read shows no counts rather than an error.
    retry: false,
  });
  const { pathname } = useLocation();
  if (session === null) return null;

  const items = visibleNavItems(session.user.role);
  const account = items.find((item) => item.section === 'account');
  const zone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const initials = session.user.email.slice(0, 2).toUpperCase();
  const counts = countsOf(overview.data);
  const waiting = counts.get('/') ?? 0;
  const here = crumbFor(pathname, items);
  return (
    <div className="app">
      {/* First thing a keyboard reaches: past the whole navigation in one press. */}
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <div className="sidebar">
        <div className="brand">
          <span className="brand-tile" aria-hidden="true">
            <ShieldIcon className="brand-mark" />
          </span>
          <span>
            Bartizo
            <span className="brand-sub">Fleet security</span>
          </span>
        </div>

        <nav className="nav" aria-label="Main">
          {SECTIONS.map((section) => {
            const entries = items.filter((item) => item.section === section);
            if (entries.length === 0) return null;
            return (
              <div key={section} className="nav-section">
                <h2 className="nav-heading">{NAV_SECTION_LABEL[section]}</h2>
                <ul>
                  {entries.map((item) => (
                    <NavEntry key={item.to} item={item} count={counts.get(item.to)} />
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
      </div>

      <div className="content">
        <header className="topbar">
          <nav className="crumbs" aria-label="Where you are">
            {here.section !== undefined && (
              <>
                <span className="crumb-section">{here.section}</span>
                <span aria-hidden="true">›</span>
              </>
            )}
            <span className="crumb-current">{here.label}</span>
          </nav>

          <div className="topbar-actions">
            <NavLink
              to="/"
              end
              className="topbar-attention"
              title={
                waiting === 0
                  ? 'Nothing needs a decision'
                  : `${String(waiting)} waiting on the Overview`
              }
            >
              <BellIcon aria-hidden="true" />
              <span className="visually-hidden">
                {waiting === 0 ? 'Nothing needs a decision' : `${String(waiting)} waiting`}
              </span>
              {waiting > 0 && (
                <span className="nav-count" aria-hidden="true">
                  {waiting > 99 ? '99+' : waiting}
                </span>
              )}
            </NavLink>
            {/* Section 20: times are shown in this zone and stored in UTC. */}
            <span className="topbar-zone" title="Times are shown in this time zone">
              {zone}
            </span>
            <ThemeSwitch />
            <NavLink to={account?.to ?? '/account'} className="identity">
              <span className="avatar" aria-hidden="true">
                {initials}
              </span>
              <span className="identity-text">
                <span className="identity-email">{session.user.email}</span>
                <span className="identity-meta">{session.user.role}</span>
              </span>
            </NavLink>
            <Button
              variant="ghost"
              onClick={() => {
                void signOut();
              }}
            >
              <LogOutIcon />
              <span className="topbar-signout">Sign out</span>
            </Button>
          </div>
        </header>

        <main className="main" id="main" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}
